import React, { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { Paginator } from "primereact/paginator";
import { Dropdown } from "primereact/dropdown";
import { Spacer, MediumText, SmallText, FlexRow, StyledGridWrapper } from "../Utility";
import { Input } from "@vds/inputs";
import { Tooltip } from "@vds/tooltips";

const StyledDropdown = styled(Dropdown)`
  width: 25px;
`;

const Wrapper = styled.div`
  .p-paginator {
    justify-content: flex-start;
    background: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.text};
    height: 50px;
    paddinf-right: 0px;
    padding-left: 0px;
  }
  .p-paginator-current {
    align-items: end;
    padding: 0 0.5rem 5px;
    color: ${({ theme }) => theme.text};
  }
  .p-paginator .p-paginator-pages .p-paginator-page.p-highlight,
  .p-link:focus {
    box-shadow: none !important;
    background: transparent;
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 0;
    outline: none !important;
  }
  .p-dropdown:not(.p-disabled):hover,
  .p-dropdown:not(.p-disabled).p-focus {
    border-color: black;
    box-shadow: none !important;
  }
  .p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
    background: transparent;
    border-left: 4px solid #d52b1e;
    padding-left: 12px;
  }
  .p-dropdown-panel .p-dropdown-items .p-dropdown-item {
    border-bottom: 1px solid lightgrey;
  }
  .p-dropdown-panel .p-dropdown-items .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover {
    background: transparent;
    border-left: 4px solid black;
    padding-left: 12px;
  }
  .p-dropdown-panel .p-dropdown-items {
    padding: 0;
    border: 1px solid black;
  }
  .p-paginator .p-dropdown,
  .p-paginator .p-dropdown:hover {
    width: 70px !important;
    border-radius: 0 !important;
    border: 1px solid lightgrey !important;
    border-bottom-color: black !important;
    &.p-focus {
      border-color: black !important;
    }
  }
  .p-dropdown .p-dropdown-trigger,
  .p-dropdown-item,
  .p-dropdown-label,
  .p-link {
    width: 2.5rem;
    color: ${({ theme }) => theme.text} !important;
  }
  .p-dropdown {
    height: 43px;
  }
`;

const InlineSpan = styled.span`
  display: inline-flex;
  align-items: center;
`;

const InlineInput = styled(Input)`
  display: inline-flex !important;
`;

export const SLPaginator = ({ itemsPerPageDropDownValues, totalRecords, onPageChangeHandler, onPageInputChangeHandler }) => {
  const paginatorAriaLabel = {};
  const [currentPage, setCurrentPage] = useState(1);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [paginatorError, setPaginatorError] = useState(false);
  const [paginatorErrorText, setPaginatorErrorText] = useState("Press 'Enter' key to go to this page.");

  useEffect(() => {
    setRows(itemsPerPageDropDownValues ? itemsPerPageDropDownValues[0]?.value : 10);
  }, []);

  const onPageInputKeyDown = (event, options) => {
    if (event.key === "Enter") {
      const page = parseInt(currentPage);
      if (page < 1 || page > options.totalPages) {
        setPaginatorError(true);
        setPaginatorErrorText(`Value must be between 1 and ${options.totalPages}.`);
      } else {
        const firstPage = currentPage ? options.rows * (page - 1) : 0;
        setFirst(firstPage);
        setPaginatorError(false);
        setPaginatorErrorText("Press 'Enter' key to go to this page.");
      }
      onPageInputChangeHandler(event, options);
    }
  };

  const onPageInputChange = (event) => {
    setCurrentPage(event.target.value);
  };

  const onPageChange = (event) => {
    setFirst(event.first);
    setCurrentPage(event.page + 1);
    setPaginatorError(false);
    setPaginatorErrorText("Press 'Enter' key to go to this page.");
    onPageChangeHandler(event);
  };

  const onChangeDropDown = (event, options) => {
    setRows(event?.value);
  };

  const pageTemplate = {
    layout: "CurrentPageReport RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
    RowsPerPageDropdown: (options) => {
      return (
        <React.Fragment>
          <MediumText primitive="span">Items per page: </MediumText>
          <Dropdown
            value={rows}
            options={itemsPerPageDropDownValues}
            onChange={(e) => onChangeDropDown(e, options)}
            ariaLabel={paginatorAriaLabel?.itemsPerPageLabel}
          />
        </React.Fragment>
      );
    },
    CurrentPageReport: (options) => {
      return (
        <FlexRow
          flexGrow="1"
          css={{
            "@media only screen and (max-width: 950px)": {
              flexBasis: "100%",
            },
          }}
        >
          <InlineSpan>
            <Spacer right="15px">
              <MediumText css={{ display: "inline-block" }} primitive="span">
                Go to:
              </MediumText>
              <InlineInput
                width="25px"
                error={paginatorError}
                value={currentPage}
                onKeyDown={(e) => onPageInputKeyDown(e, options)}
                onChange={onPageInputChange}
                aria-label={paginatorAriaLabel?.goToLabel}
              />
              {paginatorError && <Tooltip title={paginatorErrorText} size="medium" surface="light" />}
            </Spacer>
            <Spacer right="15px">
              <MediumText css={{ display: "inline-block" }} primitive="span">
                Displaying items {options.first} - {options.last} of {options.totalRecords}
              </MediumText>
            </Spacer>
          </InlineSpan>
        </FlexRow>
      );
    },
    PageLinks: (options) => {
      return (
        <button {...options} role={paginatorAriaLabel?.pageNumberLabel && "button"} aria-label={paginatorAriaLabel?.pageNumberLabel} tabIndex="0">
          {options.element.props.children}
        </button>
      );
    },
  };
  return (
    <Wrapper>
      <Paginator template={pageTemplate} first={first} rows={rows} totalRecords={totalRecords} onPageChange={onPageChange} />
    </Wrapper>
  );
};
